import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { FlaskConical, ListChecks, ListX, Loader2, SearchX, Trash2, X } from 'lucide-react';
import { memo, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { COMPARE_PART_LABEL_KEYS } from '@/atoms/deviation-progress.atom';
import { layersAtom, selectedLayerIdsAtom, type LayerData } from '@/atoms/layers.atom';
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog';
import { BulkDeletionStatus } from '@/components/BulkDeletionStatus';
import { Button } from '@/components/ui/button';
import { execute } from '@/graphql/execute';
import type { ProcessJobType } from '@/graphql/graphql';
import type { RerunChoice, RerunChoiceKey } from '@/lib/analyses/comparisonRerunChoices';
import { landxmlMergeWarningTexts, type LandxmlMergeStatsWarnings } from '@/lib/analyses/landxmlMergeWarnings';
import { isComparison, isDeleteWarned, mergedRunBatchIds, mergeRunFiles, mergeRuns, type MergedRun } from '@/lib/analyses/mergeRuns';
import { getAnalysis } from '@/lib/analyses/registry';
import type { AnalysisId } from '@/lib/analyses/types';
import {
  analysisTypesPresent,
  countRunStatuses,
  EMPTY_RUN_FILES,
  EMPTY_RUN_FILTERS,
  filterRunRows,
  groupRunsByDay,
  type RunDayGroup,
  type AnalysisRunFileItem,
  type RunFiles,
  type RunFilters,
  type RunRow,
} from '@/lib/analyses/runList';
import { analysisIdForJobType, isRunActive } from '@/lib/analyses/runs';
import { translateKey } from '@/lib/analyses/translateKey';
import { useAnalysisAvailability } from '@/lib/analyses/useAnalysisAvailability';
import { useAnalysisRunFiles, useDownloadRunFile } from '@/lib/analyses/useAnalysisRunFiles';
import { useAnalysisRuns } from '@/lib/analyses/useAnalysisRuns';
import { useComparisonRerun } from '@/lib/analyses/useComparisonRerun';
import { useLaunchAnalysis } from '@/lib/analyses/useLaunchAnalysis';
import { useDeleteAnalysisRun } from '@/lib/analyses/useDeleteAnalysisRun';
import { getBatches } from '@/queries/attachment.query';

import { AnalysisRunContextMenu } from './AnalysisRunContextMenu';
import { AnalysisRunDetailsDrawer } from './AnalysisRunDetailsDrawer';
import { AnalysisRunRow } from './AnalysisRunRow';
import { AnalysisRunToolbar } from './AnalysisRunToolbar';
import { sameRerunChoices } from './ComparisonRerunMenu';
import { partResultsOf, runPartsOf, type PartResult } from './RunPartTags';

/** Idle tick: day headings ("Today" after midnight) and "N minutes ago". */
export const LIST_CLOCK_IDLE_MS = 60_000;
/** Fast tick while a run is active: "Running · 3 min". */
export const LIST_CLOCK_ACTIVE_MS = 15_000;

/**
 * Wall clock of the list, for elapsed times, relative times and the
 * Today/Yesterday split. The runs poll does not re-render when nothing
 * changed (structural sharing), so time needs its own clock: once a minute
 * while the list is mounted, every 15 s while `isFast(now)` — a run that is
 * active per `isRunActive`, so a batch stuck WAITING for days does not keep the
 * fast tick (and a whole-list re-render every 15 s) alive forever.
 */
function useListClock(isFast: (nowMs: number) => boolean): number {
  const [now, setNow] = useState(() => Date.now());
  const intervalMs = isFast(now) ? LIST_CLOCK_ACTIVE_MS : LIST_CLOCK_IDLE_MS;
  useEffect(() => {
    // Re-read on a rate change too: a run that just started shows its elapsed
    // time from the real now, not from a clock up to a minute old.
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

function DayHeading({ group, nowMs }: { group: RunDayGroup<RunRow>; nowMs: number }) {
  const { t, i18n } = useTranslation('project');
  const label =
    group.kind === 'today'
      ? t('analyses.today')
      : group.kind === 'yesterday'
        ? t('analyses.yesterday')
        : group.date.toLocaleDateString(i18n.language, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: group.date.getFullYear() === new Date(nowMs).getFullYear() ? undefined : 'numeric',
          });
  return (
    <h2
      data-testid={`analysis-runs-day-${group.key}`}
      className='border-b border-slate-200/70 bg-slate-50/80 px-4 py-1.5 text-xs font-medium text-slate-500 dark:border-stone-700/40 dark:bg-stone-800/40 dark:text-stone-400'>
      {label}
    </h2>
  );
}

const box = 'overflow-hidden rounded-md border border-slate-200/70 bg-white dark:border-stone-800 dark:bg-stone-900';

interface RowActions {
  openDetails: (runId: string) => void;
  openResult: (layerId: string) => void;
  /** Every batch of the row: a paired comparison has two. `active` adds the
   *  still-running warning to the confirmation. */
  confirmDelete: (batchIds: string[], options: { active: boolean }) => void;
  toggleSelection: (batchIds: string[]) => void;
  launch: (id: AnalysisId, options: { rerunJobId: string; rerunJobType: ProcessJobType }) => void;
  rerunComparison: (run: MergedRun, key: RerunChoiceKey) => void;
  download: (file: AnalysisRunFileItem) => void;
}

const NO_CHOICES: readonly RerunChoice[] = [];
const NO_PART_RESULTS: readonly PartResult[] = [];

/** The URL changes when a drawer opens, but the list rows do not. Keep each
 * row's menus and tooltips mounted without rendering them again. The ref lets
 * handlers use the latest selection, navigation and permission state. */
const RunListItem = memo(function RunListItem({
  row, files, nowMs, layersById, href, warningCount, mergeSourcesCleaned,
  canRerun, canDelete, isDeleting, selectionMode, selected,
  selectionDisabled, selectionDisabledTitle, rerunChoices, actions,
}: {
  row: RunRow;
  files: RunFiles;
  nowMs: number;
  layersById: ReadonlyMap<string, LayerData>;
  href: string;
  warningCount: number;
  mergeSourcesCleaned: boolean;
  canRerun: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  selectionMode: boolean;
  selected: boolean;
  selectionDisabled: boolean;
  selectionDisabledTitle?: string;
  rerunChoices: readonly RerunChoice[];
  actions: RefObject<RowActions>;
}) {
  const { run, id } = row;
  const resultLayer = run.resultLayerId ? layersById.get(run.resultLayerId) : undefined;
  const batchIds = mergedRunBatchIds(run);
  const onDelete = () => actions.current.confirmDelete(batchIds, { active: isDeleteWarned(run) });
  const onRerun = () => actions.current.launch(id, { rerunJobId: run.id, rerunJobType: run.type });
  const onRerunChoice = (key: RerunChoiceKey) => actions.current.rerunComparison(run, key);
  const onOpenDetails = () => actions.current.openDetails(run.id);
  const onDownload = (file: AnalysisRunFileItem) => actions.current.download(file);
  return (
    <AnalysisRunRow
      row={row}
      files={files}
      nowMs={nowMs}
      resultLayerName={resultLayer?.name}
      warningCount={warningCount}
      mergeSourcesCleaned={mergeSourcesCleaned}
      canRerun={canRerun}
      canDelete={canDelete && batchIds.length > 0}
      isDeleting={isDeleting}
      onDelete={onDelete}
      selectionMode={selectionMode}
      selected={selected}
      selectionDisabled={selectionDisabled}
      selectionDisabledTitle={selectionDisabledTitle}
      onSelectionChange={() => actions.current.toggleSelection(batchIds)}
      onRerun={onRerun}
      onOpenResult={() => resultLayer && actions.current.openResult(resultLayer.id)}
      onDownload={onDownload}
      onOpenDetails={onOpenDetails}
      rerunChoices={rerunChoices}
      onRerunChoice={onRerunChoice}
      partResults={isComparison(run) ? partResultsOf(run, layersById) : NO_PART_RESULTS}
      onOpenLayer={(layerId) => actions.current.openResult(layerId)}
      contextMenu={
        <AnalysisRunContextMenu
          row={row}
          files={files}
          layersById={layersById}
          href={href}
          canRerun={canRerun}
          canDelete={canDelete}
          isDeleting={isDeleting}
          onOpenDetails={onOpenDetails}
          onRerun={onRerun}
          onOpenLayer={(layerId) => actions.current.openResult(layerId)}
          onDownload={onDownload}
          onDelete={onDelete}
          rerunChoices={rerunChoices}
          onRerunChoice={onRerunChoice}
        />
      }
    />
  );
}, (a, b) =>
  a.row.run === b.row.run &&
  a.row.id === b.row.id &&
  a.row.title === b.row.title &&
  a.row.inputNames === b.row.inputNames &&
  a.row.tags === b.row.tags &&
  a.files === b.files &&
  a.nowMs === b.nowMs &&
  a.layersById === b.layersById &&
  a.href === b.href &&
  a.warningCount === b.warningCount &&
  a.mergeSourcesCleaned === b.mergeSourcesCleaned &&
  a.canRerun === b.canRerun &&
  a.canDelete === b.canDelete &&
  a.isDeleting === b.isDeleting &&
  a.selectionMode === b.selectionMode &&
  a.selected === b.selected &&
  a.selectionDisabled === b.selectionDisabled &&
  a.selectionDisabledTitle === b.selectionDisabledTitle &&
  sameRerunChoices(a.rerunChoices, b.rerunChoices) &&
  a.actions === b.actions,
);

/**
 * The Analyses hub (D12; page pass 2026-10-10): every server-job analysis run
 * of the project, filterable, grouped by day, newest first, with each run's
 * downloads. Rows are shown to everyone; Re-run only where the analysis is
 * visible to this user (flag / role / shared link), and a locked analysis
 * still re-runs into the upgrade prompt via `useLaunchAnalysis`.
 *
 * `newAnalysis` is the page's "New analysis" control, repeated in the empty
 * state (omitted when the user cannot start one).
 */
export function AnalysisRunList({
  projectId,
  href,
  newAnalysis,
}: {
  projectId: string;
  href: string;
  newAnalysis?: ReactNode;
}) {
  const { t } = useTranslation('project');
  const { data: runs, isLoading } = useAnalysisRuns(projectId);
  const { data: filesByRun } = useAnalysisRunFiles(projectId, runs);
  const download = useDownloadRunFile();
  const layers = useAtomValue(layersAtom);
  const setSelectedLayerIds = useSetAtom(selectedLayerIdsAtom(projectId));
  const navigate = useNavigate();
  const launch = useLaunchAnalysis();
  const availability = useAnalysisAvailability();
  const comparisonRerun = useComparisonRerun();
  const [filters, setFilters] = useState<RunFilters>(EMPTY_RUN_FILTERS);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedBatchIds, setSelectedBatchIds] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const detailsRunId = searchParams.get('run');

  const layersById = useMemo(() => new Map(layers.map((l) => [l.id, l])), [layers]);
  // A paired comparison (two runs, one groupId) is one row (spec §4.3).
  // Merged on `runs` alone, so a row's `run` keeps its identity across
  // renders (the memoised rows compare it).
  const merged = useMemo(() => mergeRuns(runs ?? []), [runs]);
  const rows = useMemo<RunRow[]>(
    () =>
      merged.flatMap((run) => {
        const id = analysisIdForJobType(run.type);
        if (!id) return [];
        return [
          {
            run,
            id,
            title: translateKey(t, getAnalysis(id).titleKey),
            inputNames: run.inputLayerIds.map((lid) => layersById.get(lid)?.name ?? '—').join(' · '),
            tags: runPartsOf(run).map(({ part }) => translateKey(t, COMPARE_PART_LABEL_KEYS[part])).join(' · '),
          },
        ];
      }),
    [merged, layersById, t],
  );
  // One files entry per row: a comparison's joins its parts' (a shared report
  // once). Stable per row while the inputs are, for the memoised rows.
  const filesByRow = useMemo(
    () => new Map(rows.map((r) => [r.run.id, mergeRunFiles(r.run, filesByRun)])),
    [rows, filesByRun],
  );
  const now = useListClock((nowMs) => rows.some((r) => isRunActive(r.run, nowMs)));
  const counts = useMemo(() => countRunStatuses(rows), [rows]);
  const types = useMemo(() => analysisTypesPresent(rows), [rows]);
  const groups = useMemo(() => groupRunsByDay(filterRunRows(rows, filters), now), [rows, filters, now]);

  const openResult = (layerId: string) => {
    setSelectedLayerIds((prev) => (prev.includes(layerId) ? prev : [...prev, layerId]));
    navigate(`${href}?layers=true`);
  };

  // The open run lives in `?run=` (linkable; Back closes it). Opening pushes an
  // entry marked in its state, so closing one opened here goes back to the
  // entry before it; one arriving by link has nothing of ours to go back to,
  // so closing replaces it with the URL minus `run`.
  const openDetails = (runId: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('run', runId);
        return next;
      },
      { state: { runDetails: true } },
    );
  const closeDetails = () => {
    if ((location.state as { runDetails?: boolean } | null)?.runDetails) {
      navigate(-1);
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('run');
        return next;
      },
      { replace: true },
    );
  };
  // A link to either part of a comparison opens its row.
  const detailsRow = detailsRunId
    ? (rows.find((r) => r.run.parts.some((p) => p.id === detailsRunId)) ?? null)
    : null;
  const { canDelete, isDeleting, confirmDelete } = useDeleteAnalysisRun(projectId, (batchIds) => {
    if (detailsRow && mergedRunBatchIds(detailsRow.run).some((id) => batchIds.includes(id))) closeDetails();
  });
  const { data: batchesData, isError: batchesError } = useQuery({
    queryKey: ['batches', projectId],
    queryFn: () => execute(getBatches, { projectId }),
    enabled: selectionMode && canDelete,
  });
  const eligibleBatchIds = useMemo(
    () => new Set(batchesData?.batches.filter((batch) => !batch.bulkDeleteBlockedReason).map((batch) => batch.id) ?? []),
    [batchesData],
  );
  const blockedReasons = useMemo(
    () => new Map(batchesData?.batches.map((batch) => [batch.id, batch.bulkDeleteBlockedReason]) ?? []),
    [batchesData],
  );
  useEffect(() => {
    if (!selectionMode || !batchesData) return;
    const present = new Set(rows.flatMap((row) => mergedRunBatchIds(row.run)));
    setSelectedBatchIds((current) => {
      const next = new Set([...current].filter((id) => present.has(id) && eligibleBatchIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [selectionMode, batchesData, eligibleBatchIds, rows]);
  const stopSelecting = () => {
    setSelectionMode(false);
    setSelectedBatchIds(new Set());
  };
  /** A row is selectable when every batch behind it is (a comparison's two
   *  go together), and selected when all of them are. */
  const rowSelectable = (batchIds: string[]) => batchIds.length > 0 && batchIds.every((id) => eligibleBatchIds.has(id));
  const rowSelected = (batchIds: string[]) => batchIds.length > 0 && batchIds.every((id) => selectedBatchIds.has(id));
  const toggleSelection = (batchIds: string[]) => {
    if (!rowSelectable(batchIds)) return;
    setSelectedBatchIds((current) => {
      const next = new Set(current);
      const selectedNow = batchIds.every((id) => next.has(id));
      for (const id of batchIds) {
        if (selectedNow) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };
  const selectedRowCount = rows.filter((row) => rowSelected(mergedRunBatchIds(row.run))).length;
  const rowActions = useRef<RowActions>(null!);
  rowActions.current = {
    openDetails,
    openResult,
    confirmDelete,
    toggleSelection,
    launch,
    rerunComparison: comparisonRerun.rerun,
    download: (file) => void download(file),
  };
  const drawer = (
    <AnalysisRunDetailsDrawer
      projectId={projectId}
      href={href}
      runId={detailsRunId}
      row={detailsRow}
      nowMs={now}
      files={(detailsRow && filesByRow.get(detailsRow.run.id)) || EMPTY_RUN_FILES}
      filesByRun={filesByRun}
      layersById={layersById}
      canRerun={!!detailsRow && availability(detailsRow.id).visible}
      rerunChoices={comparisonRerun.choices}
      onRerunChoice={(key) => {
        if (!detailsRow) return;
        closeDetails();
        comparisonRerun.rerun(detailsRow.run, key);
      }}
      onClose={closeDetails}
      onRerun={() => {
        if (!detailsRow) return;
        closeDetails();
        launch(detailsRow.id, { rerunJobId: detailsRow.run.id, rerunJobType: detailsRow.run.type });
      }}
      onOpenLayer={openResult}
      onDownload={(file) => void download(file)}
    />
  );

  if (isLoading) {
    return (
      <div className={box}>
        <div className='flex items-center gap-2 px-4 py-6 text-sm text-slate-600 dark:text-stone-400'>
          <Loader2 className='size-4 animate-spin' />
          {t('analysisLauncher.chooser.loading')}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div>
        <div className={box}>
          <div data-testid='analysis-runs-empty' className='flex flex-col items-center gap-2 px-6 py-12 text-center'>
            <span className='mb-1 flex size-10 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-stone-800 dark:text-stone-400'>
              <FlaskConical className='size-5' aria-hidden />
            </span>
            <div className='text-sm font-medium text-slate-900 dark:text-stone-100'>{t('analyses.emptyTitle')}</div>
            <p className='max-w-sm text-sm text-slate-500 dark:text-stone-400'>{t('analyses.emptyBody')}</p>
            {newAnalysis && <div className='mt-2'>{newAnalysis}</div>}
          </div>
          {detailsRunId && drawer}
        </div>
        {canDelete && <BulkDeletionStatus projectId={projectId} />}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <div className='min-w-0 flex-1'>
          <AnalysisRunToolbar filters={filters} onChange={setFilters} counts={counts} types={types} />
        </div>
        {canDelete && (!selectionMode ? (
          <Button data-testid='analysis-runs-select' type='button' variant='ghost' size='sm'
            onClick={() => setSelectionMode(true)}>
            <ListChecks className='size-4' />
            {t('bulkDelete.selectAnalyses')}
          </Button>
        ) : (
          <div className='flex items-center gap-1'>
            <Button data-testid='analysis-runs-select-all' type='button' variant='ghost' size='sm'
              onClick={() => setSelectedBatchIds(new Set(groups.flatMap((group) => group.rows)
                .map((row) => mergedRunBatchIds(row.run))
                .filter(rowSelectable)
                .flat()))}>
              {t('bulkDelete.selectAll')}
            </Button>
            <Button data-testid='analysis-runs-clear-selection' type='button' variant='ghost' size='icon'
              className='size-8' aria-label={t('bulkDelete.clearSelection')}
              disabled={!selectedBatchIds.size} onClick={() => setSelectedBatchIds(new Set())}>
              <ListX className='size-4' />
            </Button>
            <Button data-testid='analysis-runs-delete-selected' type='button' variant='destructive' size='sm'
              disabled={!selectedBatchIds.size} onClick={() => setBulkDialogOpen(true)}>
              <Trash2 className='size-4' />
              {t('bulkDelete.deleteSelected', { count: selectedRowCount })}
            </Button>
            <Button data-testid='analysis-runs-cancel-selection' type='button' variant='ghost' size='icon'
              className='size-8' aria-label={t('bulkDelete.cancel')} onClick={stopSelecting}>
              <X className='size-4' />
            </Button>
          </div>
        ))}
      </div>
      {selectionMode && batchesError && <p role='alert' className='text-sm text-destructive'>{t('bulkDelete.previewUnavailable')}</p>}
      {groups.length === 0 ? (
        <div className={box}>
          <div data-testid='analysis-runs-no-matches' className='flex flex-col items-center gap-3 px-6 py-10 text-center'>
            <SearchX className='size-5 text-slate-400 dark:text-stone-500' aria-hidden />
            <p className='text-sm text-slate-600 dark:text-stone-400'>{t('analyses.noMatches')}</p>
            <Button
              type='button'
              size='sm'
              variant='outline'
              data-testid='analysis-runs-clear-filters'
              onClick={() => setFilters(EMPTY_RUN_FILTERS)}>
              {t('analyses.clearFilters')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={box} data-testid='analysis-runs'>
          {groups.map((group) => (
            <section
              key={group.key}
              className='border-b border-slate-200/70 last:border-b-0 dark:border-stone-700/40'>
              <DayHeading group={group} nowMs={now} />
              {group.rows.map((row) => {
                const { run, id } = row;
                const resultLayer = run.resultLayerId ? layersById.get(run.resultLayerId) : undefined;
                const resultMetadata = resultLayer?.metadata as {
                  sourceCleanupOperationId?: string;
                  landxmlMergeStats?: LandxmlMergeStatsWarnings;
                } | null;
                const warningCount = id === 'landxmlMerge'
                  ? landxmlMergeWarningTexts(resultMetadata?.landxmlMergeStats, t).length : 0;
                const batchIds = mergedRunBatchIds(run);
                const blocked = batchIds.map((b) => blockedReasons.get(b)).find((reason) => reason != null);
                return (
                  <RunListItem
                    key={run.id}
                    row={row}
                    files={filesByRow.get(run.id) ?? EMPTY_RUN_FILES}
                    nowMs={now}
                    layersById={layersById}
                    href={href}
                    warningCount={warningCount}
                    mergeSourcesCleaned={id === 'landxmlMerge' && !!resultMetadata?.sourceCleanupOperationId}
                    canRerun={availability(id).visible}
                    canDelete={canDelete}
                    isDeleting={isDeleting}
                    selectionMode={selectionMode}
                    selected={rowSelected(batchIds)}
                    selectionDisabled={!rowSelectable(batchIds)}
                    selectionDisabledTitle={blocked
                      ? blocked === 'ACTIVE_JOB' ? t('bulkDelete.activeJob') : t('bulkDelete.activeStatus')
                      : undefined}
                    rerunChoices={isComparison(run) ? comparisonRerun.choices : NO_CHOICES}
                    actions={rowActions}
                  />
                );
              })}
            </section>
          ))}
        </div>
      )}
      {drawer}
      {canDelete && <BulkDeletionStatus projectId={projectId} />}
      <BulkDeleteDialog projectId={projectId} kind='BATCHES' selectedIds={[...selectedBatchIds]}
        selectedSummary={selectedBatchIds.size === selectedRowCount
          ? t('bulkDelete.selectedAnalyses', { count: selectedRowCount })
          : t('bulkDelete.selectedAnalysesWithBatches', { count: selectedRowCount, batches: selectedBatchIds.size })}
        open={bulkDialogOpen} onOpenChange={setBulkDialogOpen} onComplete={stopSelecting} />
    </div>
  );
}
